/**
 * CampaignRecallsPanel
 * ──────────────────────────────────────────────────────────────────────
 * Recalls tied to custom provider-defined campaigns (e.g., APCM, CCM,
 * Wellness, Allergy shots, Flu vaccines).
 *
 *   • Campaign picker (cards) — switch which campaign's roster is shown.
 *   • Rule builder (e.g., "not seen in last 9 months") with live count.
 *   • Patient group chips — Annual wellness, Allergy, Flu, etc. — each
 *     filters the roster.
 *   • Demographics view (age bands + sex split) per campaign.
 *   • CSV export of the current filtered roster.
 *
 * Demo-only. All data is local fixtures.
 */
import { useMemo, useState } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';
import {
  Users, Download, Plus, Activity, Heart, Syringe, Shield, Calendar, Sparkles,
  Filter, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../../contexts/AppModeContext';

const CAMPAIGNS = [
  { id: 'apcm', name: 'APCM · Advanced Primary Care Management', icon: Heart, accent: '#ef4444', desc: 'Chronic-care eligible, ≥ 2 conditions, CMS-reimbursable monthly touch-point.' },
  { id: 'ccm', name: 'CCM · Chronic Care Management', icon: Activity, accent: '#6b5ce7', desc: 'Enrolled CCM patients — 20-min / month care plan outreach.' },
  { id: 'wellness', name: 'Annual Wellness', icon: Calendar, accent: '#22c55e', desc: 'Medicare AWV overdue patients, prep + book.' },
  { id: 'allergy', name: 'Allergy Shots', icon: Syringe, accent: '#f59e0b', desc: 'Immunotherapy patients on weekly/biweekly schedule.' },
  { id: 'flu', name: 'Flu Vaccine', icon: Shield, accent: '#0ea5e9', desc: 'Seasonal flu — eligible panel with no dose logged this season.' },
];

const RULES = [
  { id: 'not-seen-9mo', label: 'Not seen in last 9 months', kind: 'inactivity' },
  { id: 'not-seen-12mo', label: 'Not seen in last 12 months', kind: 'inactivity' },
  { id: 'dx-htn', label: 'Dx: Hypertension (I10)', kind: 'dx' },
  { id: 'dx-dm2', label: 'Dx: Type-2 Diabetes (E11.9)', kind: 'dx' },
  { id: 'age-65plus', label: 'Age ≥ 65', kind: 'demo' },
  { id: 'medicare', label: 'Insurance: Medicare', kind: 'payer' },
];

const GROUPS = [
  { id: 'annual', label: 'Annual wellness' },
  { id: 'allergy', label: 'Allergy' },
  { id: 'flu', label: 'Flu' },
  { id: 'copd', label: 'COPD panel' },
  { id: 'diabetes', label: 'Diabetes panel' },
  { id: 'postpartum', label: 'Postpartum' },
];

/* ── Demo roster — small enough to be scanable, realistic enough to feel live ── */
const DEMO_ROSTER = [
  { id: 'p1', name: 'Emily Davis', age: 47, sex: 'F', lastSeen: '2025-07-12', campaigns: ['ccm', 'wellness'], groups: ['annual', 'diabetes'], dx: ['dx-dm2'], insurance: 'Aetna' },
  { id: 'p2', name: 'James Taylor', age: 55, sex: 'M', lastSeen: '2025-04-02', campaigns: ['apcm', 'ccm'], groups: ['diabetes', 'copd'], dx: ['dx-dm2', 'dx-htn'], insurance: 'United' },
  { id: 'p3', name: 'Robert Johnson', age: 68, sex: 'M', lastSeen: '2025-02-15', campaigns: ['apcm', 'wellness'], groups: ['copd'], dx: ['dx-htn'], insurance: 'Medicare' },
  { id: 'p4', name: 'Sarah Williams', age: 36, sex: 'F', lastSeen: '2025-10-03', campaigns: ['allergy'], groups: ['allergy'], dx: [], insurance: 'Cigna' },
  { id: 'p5', name: 'Maria Garcia', age: 31, sex: 'F', lastSeen: '2025-09-28', campaigns: ['flu'], groups: ['flu'], dx: [], insurance: 'Blue Cross' },
  { id: 'p6', name: 'Lisa Brown', age: 50, sex: 'F', lastSeen: '2025-06-20', campaigns: ['ccm', 'flu'], groups: ['diabetes', 'flu'], dx: ['dx-dm2'], insurance: 'Blue Cross' },
  { id: 'p7', name: 'David Wilson', age: 43, sex: 'M', lastSeen: '2024-11-14', campaigns: ['apcm'], groups: [], dx: ['dx-htn'], insurance: 'Aetna' },
  { id: 'p8', name: 'Nathan Ruiz', age: 72, sex: 'M', lastSeen: '2025-05-01', campaigns: ['ccm', 'wellness', 'flu'], groups: ['annual'], dx: ['dx-htn'], insurance: 'Medicare' },
  { id: 'p9', name: 'Jessica Chen', age: 29, sex: 'F', lastSeen: '2025-12-11', campaigns: ['allergy'], groups: ['allergy'], dx: [], insurance: 'United' },
  { id: 'p10', name: 'Tom Kowalski', age: 61, sex: 'M', lastSeen: '2025-03-18', campaigns: ['apcm', 'wellness'], groups: ['annual'], dx: ['dx-htn'], insurance: 'Medicare' },
];

const AGE_BANDS = [
  { label: '18-34', min: 18, max: 34 },
  { label: '35-49', min: 35, max: 49 },
  { label: '50-64', min: 50, max: 64 },
  { label: '65+', min: 65, max: 200 },
];

const monthsSince = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  const then = new Date(y, m - 1, d);
  const now = new Date();
  return (now.getFullYear() - then.getFullYear()) * 12 + (now.getMonth() - then.getMonth());
};

const CampaignRecallsPanel = () => {
  const { isDemo } = useAppMode();
  const [campaignId, setCampaignId] = useState('apcm');
  const [selectedRules, setSelectedRules] = useState(['not-seen-9mo']);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const campaign = CAMPAIGNS.find((c) => c.id === campaignId);

  const roster = useMemo(() => DEMO_ROSTER.filter((p) => {
    if (!p.campaigns.includes(campaignId)) return false;
    if (selectedGroup && !p.groups.includes(selectedGroup)) return false;
    if (selectedRules.length === 0) return true;
    return selectedRules.every((r) => {
      if (r === 'not-seen-9mo') return monthsSince(p.lastSeen) >= 9;
      if (r === 'not-seen-12mo') return monthsSince(p.lastSeen) >= 12;
      if (r === 'age-65plus') return p.age >= 65;
      if (r === 'medicare') return p.insurance === 'Medicare';
      if (r.startsWith('dx-')) return p.dx.includes(r);
      return true;
    });
  }), [campaignId, selectedRules, selectedGroup]);

  /* Demographics breakdown */
  const demo = useMemo(() => {
    const total = roster.length || 1;
    const byAge = AGE_BANDS.map((b) => ({
      ...b,
      count: roster.filter((p) => p.age >= b.min && p.age <= b.max).length,
    }));
    const female = roster.filter((p) => p.sex === 'F').length;
    const male = roster.filter((p) => p.sex === 'M').length;
    return { byAge, total, female, male, femalePct: Math.round((female / total) * 100), malePct: Math.round((male / total) * 100) };
  }, [roster]);

  const exportCsv = () => {
    const esc = (v) => `"${String(v || '').replace(/"/g, '""')}"`;
    const header = 'Patient,Age,Sex,Last seen,Months since,Insurance';
    const body = roster.map((p) => [esc(p.name), p.age, p.sex, p.lastSeen, monthsSince(p.lastSeen), esc(p.insurance)].join(',')).join('\n');
    const blob = new Blob([`${header}\n${body}\n`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recall-${campaignId}-roster.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${roster.length} patient(s) · ${campaign.name.split(' ')[0]}`);
  };

  const sendOutreach = () => {
    toast.success(`Queued outreach for ${roster.length} patient(s)`, {
      description: `Campaign: ${campaign.name.split(' ·')[0]} · Rules: ${selectedRules.length}`,
    });
  };

  const toggleRule = (id) => {
    setSelectedRules((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  };

  return (
    <div className="space-y-4" data-testid="campaign-recalls-panel">
      {/* Campaign picker */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold text-slate-800">Recall campaigns</p>
          <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => toast.info('Custom campaign builder coming soon')} data-testid="campaign-new-btn">
            <Plus className="h-3 w-3" /> New campaign
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="campaign-grid">
          {CAMPAIGNS.map((c) => {
            const on = c.id === campaignId;
            const count = DEMO_ROSTER.filter((p) => p.campaigns.includes(c.id)).length;
            const Icon = c.icon;
            return (
              <button
                key={c.id}
                onClick={() => setCampaignId(c.id)}
                className={cn(
                  'text-left p-3 rounded-xl border transition-all',
                  on ? 'border-slate-900 bg-slate-50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'
                )}
                style={{ cursor: 'pointer' }}
                data-testid={`campaign-${c.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="h-7 w-7 rounded-md inline-flex items-center justify-center" style={{ background: `${c.accent}1a` }}>
                    <Icon className="h-3.5 w-3.5" style={{ color: c.accent }} />
                  </div>
                  <Badge className="text-[9px] border border-slate-200 bg-slate-100 text-slate-700">{count}</Badge>
                </div>
                <p className="text-[11px] font-semibold text-slate-900 mt-2">{c.name.split(' · ')[0]}</p>
                <p className="text-[9px] text-slate-500 mt-0.5 line-clamp-2">{c.name.split(' · ')[1] || ''}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Campaign details + rules */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="campaign-detail">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <campaign.icon className="h-4 w-4" style={{ color: campaign.accent }} />
            <div>
              <p className="text-[13px] font-semibold text-slate-900">{campaign.name}</p>
              <p className="text-[10px] text-slate-500">{campaign.desc}</p>
            </div>
          </div>
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={exportCsv} data-testid="campaign-export-csv">
              <Download className="h-3 w-3" /> CSV
            </Button>
            <Button size="sm" className="h-7 text-[10px] bg-[#111] hover:bg-[#222] gap-1" onClick={sendOutreach} data-testid="campaign-send-outreach">
              <Sparkles className="h-3 w-3" /> Send outreach ({roster.length})
            </Button>
          </div>
        </div>

        {/* Rule builder */}
        <div className="space-y-1.5">
          <p className="text-[9px] uppercase font-semibold text-slate-400 inline-flex items-center gap-1">
            <Filter className="h-2.5 w-2.5" /> Recall triggers
          </p>
          <div className="flex items-center gap-1.5 flex-wrap">
            {RULES.map((r) => {
              const on = selectedRules.includes(r.id);
              return (
                <button
                  key={r.id}
                  onClick={() => toggleRule(r.id)}
                  className={cn('text-[10px] px-2.5 py-1 rounded-full border transition-colors',
                    on ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100')}
                  style={{ cursor: 'pointer' }}
                  data-testid={`rule-${r.id}`}
                >{r.label}</button>
              );
            })}
          </div>
        </div>

        {/* Group chips */}
        <div className="space-y-1.5 mt-3">
          <p className="text-[9px] uppercase font-semibold text-slate-400 inline-flex items-center gap-1">
            <Users className="h-2.5 w-2.5" /> Patient groups
          </p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={() => setSelectedGroup(null)}
              className={cn('text-[10px] px-2.5 py-1 rounded-full border transition-colors',
                !selectedGroup ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100')}
              style={{ cursor: 'pointer' }}
              data-testid="group-all"
            >All patients</button>
            {GROUPS.map((g) => (
              <button key={g.id} onClick={() => setSelectedGroup(g.id === selectedGroup ? null : g.id)}
                className={cn('text-[10px] px-2.5 py-1 rounded-full border transition-colors',
                  selectedGroup === g.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100')}
                style={{ cursor: 'pointer' }}
                data-testid={`group-${g.id}`}
              >{g.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Demographics + roster */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Demographics card */}
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="campaign-demographics">
          <p className="text-[11px] font-semibold text-slate-800 inline-flex items-center gap-1.5">
            <TrendingUp className="h-3 w-3 text-slate-500" /> Demographics
          </p>
          <p className="text-[9px] text-slate-400 mt-0.5">{demo.total} eligible patient{demo.total === 1 ? '' : 's'}</p>
          <div className="space-y-2 mt-3">
            {demo.byAge.map((b) => {
              const pct = demo.total ? Math.round((b.count / demo.total) * 100) : 0;
              return (
                <div key={b.label} data-testid={`demo-age-${b.label}`}>
                  <div className="flex items-center justify-between text-[10px] text-slate-600 mb-0.5">
                    <span>{b.label}</span>
                    <span className="tabular-nums">{b.count} · {pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: campaign.accent }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-center">
            <div className="p-2 rounded-lg bg-rose-50 border border-rose-100">
              <p className="text-[9px] text-rose-600 uppercase font-semibold">Female</p>
              <p className="text-[14px] font-bold text-rose-800">{demo.femalePct}%</p>
            </div>
            <div className="p-2 rounded-lg bg-sky-50 border border-sky-100">
              <p className="text-[9px] text-sky-600 uppercase font-semibold">Male</p>
              <p className="text-[14px] font-bold text-sky-800">{demo.malePct}%</p>
            </div>
          </div>
        </div>

        {/* Roster */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden lg:col-span-2" data-testid="campaign-roster">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <p className="text-[12px] font-semibold text-slate-900">Eligible roster</p>
            <Badge className="text-[9px] border border-slate-200 bg-slate-100 text-slate-700">{roster.length}</Badge>
          </div>
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-[9px] text-slate-400 uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-1.5">Patient</th>
                <th className="text-left px-3 py-1.5">Age</th>
                <th className="text-left px-3 py-1.5">Sex</th>
                <th className="text-left px-3 py-1.5">Last seen</th>
                <th className="text-left px-3 py-1.5">Gap</th>
                <th className="text-left px-3 py-1.5">Insurance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {roster.length === 0 ? (
                <tr><td colSpan={6} className="text-center px-3 py-8 text-[11px] text-slate-400">No patients match this rule set. Relax a trigger above.</td></tr>
              ) : roster.map((p) => {
                const gap = monthsSince(p.lastSeen);
                return (
                  <tr key={p.id} className="hover:bg-slate-50/60" data-testid={`roster-row-${p.id}`}>
                    <td className="px-3 py-1.5 font-medium text-slate-800">{p.name}</td>
                    <td className="px-3 py-1.5 text-slate-600">{p.age}</td>
                    <td className="px-3 py-1.5 text-slate-600">{p.sex}</td>
                    <td className="px-3 py-1.5 text-slate-600">{p.lastSeen}</td>
                    <td className="px-3 py-1.5">
                      <span className={cn('text-[10px] font-semibold', gap >= 12 ? 'text-red-600' : gap >= 9 ? 'text-amber-700' : 'text-slate-500')}>{gap} mo</span>
                    </td>
                    <td className="px-3 py-1.5 text-slate-500">{p.insurance}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CampaignRecallsPanel;
